import React, { useMemo } from 'react';
import { Search, X } from 'lucide-react';

const SearchBar = ({ searchQuery, setSearchQuery, clearSearch, isSearching, searchResults, setSelectedDate }) => {
  const handleResultClick = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    setSelectedDate(new Date(parseInt(year), parseInt(month) - 1, parseInt(day)));
    clearSearch();
  };

  const sortedTasks = useMemo(() => 
    [...searchResults.tasks].sort((a, b) => 
      (b.due_date || b.completed_date || '').localeCompare(a.due_date || a.completed_date || '')
    ), [searchResults.tasks]);

  const sortedJournals = useMemo(() => 
    [...searchResults.journals].sort((a, b) => 
      (b.date || '').localeCompare(a.date || '')
    ), [searchResults.journals]);

  return (
    <div className="flex-1 sm:max-w-md">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
        <input
          type="text"
          placeholder="Search all tasks & notes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          className="w-full pl-10 pr-10 h-9 sm:h-10 bg-white border border-slate-300 rounded-md text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          data-testid="search-input"
        />
        {searchQuery && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 z-10"
            data-testid="search-clear-btn"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        {isSearching && searchQuery.trim() && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-80 overflow-y-auto z-[9999]">
            {searchResults.tasks.length === 0 && searchResults.journals.length === 0 ? (
              <div className="p-3 text-sm text-slate-500 text-center">No results found</div>
            ) : (
              <>
                {searchResults.tasks.length > 0 && (
                  <div>
                    <div className="px-3 py-2 bg-slate-50 text-xs font-semibold text-slate-600 uppercase tracking-wide border-b">
                      Tasks ({searchResults.tasks.length})
                    </div>
                    {sortedTasks.map((task) => (
                      <button
                        key={task.id}
                        onClick={() => handleResultClick(task.due_date || task.completed_date)}
                        className="w-full px-3 py-2 text-left hover:bg-blue-50 border-b border-slate-100 last:border-b-0"
                      >
                        <div className="text-sm font-medium text-slate-900 truncate">{task.title}</div>
                        <div className="text-xs text-slate-500">
                          {task.due_date || task.completed_date} &bull; Category {task.category}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {searchResults.journals.length > 0 && (
                  <div>
                    <div className="px-3 py-2 bg-slate-50 text-xs font-semibold text-slate-600 uppercase tracking-wide border-b">
                      Journal Notes ({searchResults.journals.length})
                    </div>
                    {sortedJournals.map((journal) => (
                      <button
                        key={journal.date}
                        onClick={() => handleResultClick(journal.date)}
                        className="w-full px-3 py-2 text-left hover:bg-blue-50 border-b border-slate-100 last:border-b-0"
                      >
                        <div className="text-sm font-medium text-slate-900">{journal.date}</div>
                        <div className="text-xs text-slate-500 truncate">{journal.content.substring(0, 60)}...</div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchBar;
